import { TKV2_ORDER_ATTACHMENT_API, Tkv2OrderAttachmentBody } from '@aloft/toolkit-shared';

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { getBaseUrl } from '../../hooks/api/getBaseUrl.ts';
import {
  useGetTkv2AnalyticsAttachmentAPI,
  usePutTkv2AnalyticsAttachmentAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { getPageKey, getRouteHistoryEntry, isTrackedOrderPath } from './orderRouteTracking.ts';

/** Sentinel value for startTimeRef when the timer is paused (tab hidden). */
const TIMER_PAUSED = -1;
const MIN_TRACKED_DURATION_MS = 1000;

/**
 * Tracks time spent on each page within an order.
 * It sends the duration to the backend when the user navigates away, closes
 * the tab, refreshes, or switches to another browser tab.
 *
 * Time is accumulated across multiple visits to the same page (not overwritten).
 *
 * For normal in-app navigation, the existing React Query mutation is used.
 * For unload and visibility-change events, a raw fetch with `keepalive: true`
 * is used so the browser guarantees delivery even as the page tears down.
 */
export const PageTimeTracker = () => {
  const orderID = useParams().orderId;
  const location = useLocation();
  const route = `${location.pathname}${location.search}`;
  const { fetcherOptions } = useToolkitContext();

  const { refetch: refetchAnalytics, data: analyticsResponse } = useGetTkv2AnalyticsAttachmentAPI(
    {
      pathParams: { orderID: orderID! },
    },
    { enabled: !!orderID },
  );
  const { mutate: analyticsMutate } = usePutTkv2AnalyticsAttachmentAPI({
    onSuccess: refetchAnalytics,
  });

  const analytics = analyticsResponse?.body as Tkv2OrderAttachmentBody<'analytics'> | undefined;

  const startTimeRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>(route);

  // Cache the latest auth token so it is available synchronously during unload.
  const tokenRef = useRef<string | null>(null);
  useEffect(() => {
    fetcherOptions
      .accessTokenFn?.()
      .then((token) => {
        tokenRef.current = token;
      })
      .catch(console.error);
  });

  // Refs to the latest analytics and fetcherOptions so event-listener callbacks
  // (which close over a stale snapshot) always see the current values.
  const analyticsRef = useRef(analytics);
  const fetcherOptionsRef = useRef(fetcherOptions);
  useEffect(() => {
    analyticsRef.current = analytics;
    fetcherOptionsRef.current = fetcherOptions;
  });

  const isOrderPath = useCallback(
    (pathname: string) => isTrackedOrderPath(orderID, pathname),
    [orderID],
  );

  const buildAnalyticsBody = useCallback(
    ({
      previousPath,
      duration,
      nextPath,
    }: {
      previousPath?: string;
      duration?: number;
      nextPath?: string;
    }) => {
      const currentAnalytics = analyticsRef.current;
      const nextPageTimeMs = { ...(currentAnalytics?.pageTimeMs ?? {}) };
      const nextHistory = [...(currentAnalytics?.navigationHistory ?? [])];

      if (
        previousPath &&
        duration &&
        duration > MIN_TRACKED_DURATION_MS &&
        isOrderPath(previousPath)
      ) {
        const previousPage = getPageKey(orderID, previousPath);
        nextPageTimeMs[previousPage] = (nextPageTimeMs[previousPage] ?? 0) + duration;

        const previousIndex = nextHistory.length - 1;
        if (previousIndex >= 0) {
          const entry = nextHistory[previousIndex];
          if (entry && entry.route === getRouteHistoryEntry(orderID, previousPath)) {
            nextHistory[previousIndex] = {
              ...entry,
              durationMs: duration,
            };
          }
        }
      }

      if (nextPath && isOrderPath(nextPath)) {
        const nextRoute = getRouteHistoryEntry(orderID, nextPath);
        const last = nextHistory[nextHistory.length - 1];
        if (!last || last.route !== nextRoute) {
          nextHistory.push({
            route: nextRoute,
          });
        }
      }

      return TKV2_ORDER_ATTACHMENT_API.ANALYTICS.PUT.REQUEST.BODY.parse({
        version: 1,
        pageTimeMs: nextPageTimeMs,
        navigationHistory: nextHistory,
        commentaryGenerationEvents: currentAnalytics?.commentaryGenerationEvents ?? [],
      });
    },
    [isOrderPath, orderID],
  );

  const flushWithKeepalive = useCallback(
    (previousPath: string, duration: number) => {
      if (!tokenRef.current || !orderID) return;
      const { headers } = fetcherOptionsRef.current;
      const body = buildAnalyticsBody({
        previousPath,
        duration,
      });

      fetch(`${getBaseUrl()}/api/v2/orders/${orderID}/attachments/analytics`, {
        method: 'PUT',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenRef.current}`,
          ...headers,
        },
        body: JSON.stringify(body),
      })
        .then(() => undefined)
        .catch(() => undefined);
    },
    [buildAnalyticsBody, orderID],
  );

  useEffect(() => {
    const previousPath = currentPathRef.current;
    const now = Date.now();
    const duration = now - startTimeRef.current;
    const shouldTrackPrevious =
      startTimeRef.current !== TIMER_PAUSED && isOrderPath(previousPath) && orderID;
    const shouldTrackCurrent = isOrderPath(location.pathname) && orderID;

    if (orderID && (shouldTrackPrevious || shouldTrackCurrent)) {
      analyticsMutate({
        pathParams: { orderID },
        body: buildAnalyticsBody({
          previousPath,
          duration,
          nextPath: route,
        }),
      });
    }

    startTimeRef.current = now;
    currentPathRef.current = route;
  }, [analyticsMutate, buildAnalyticsBody, isOrderPath, orderID, route]);

  useEffect(() => {
    const handler = () => {
      if (startTimeRef.current === TIMER_PAUSED) return;
      if (!isOrderPath(currentPathRef.current)) return;
      const duration = Date.now() - startTimeRef.current;
      if (duration > MIN_TRACKED_DURATION_MS) flushWithKeepalive(currentPathRef.current, duration);
    };

    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [flushWithKeepalive, isOrderPath]);

  const pauseTimer = useCallback(() => {
    if (startTimeRef.current === TIMER_PAUSED) return;
    if (!isOrderPath(currentPathRef.current)) {
      startTimeRef.current = TIMER_PAUSED;
      return;
    }

    const duration = Date.now() - startTimeRef.current;
    if (duration > MIN_TRACKED_DURATION_MS) flushWithKeepalive(currentPathRef.current, duration);
    startTimeRef.current = TIMER_PAUSED;
  }, [flushWithKeepalive, isOrderPath]);

  const resumeTimer = useCallback(() => {
    if (startTimeRef.current === TIMER_PAUSED) startTimeRef.current = Date.now();
  }, []);

  // Pause the timer when the tab is hidden; resume when it becomes visible again.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        pauseTimer();
      } else if (document.visibilityState === 'visible') {
        resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [pauseTimer, resumeTimer]);

  // Pause the timer when the browser window loses focus (e.g. alt+tab to another
  // application); resume when the window regains focus.
  useEffect(() => {
    window.addEventListener('blur', pauseTimer);
    window.addEventListener('focus', resumeTimer);
    return () => {
      window.removeEventListener('blur', pauseTimer);
      window.removeEventListener('focus', resumeTimer);
    };
  }, [pauseTimer, resumeTimer]);

  return null;
};
